import React from 'react';
import {
  fetchCasePublicAuthorityQuery,
  saveCasePublicAuthorityQuery,
} from 'app/edozvola/cases/special-regulations-compliance/service/special-regulations-compliance.queries';
import { Field, FormikProvider, useFormik } from 'formik';
import {
  casePublicAuthorityInitialData,
  issuedActInitialData,
} from 'app/edozvola/cases/special-regulations-compliance/model/special-regulations-compliance.model';
import { useParams } from 'react-router-dom';
import { casePublicAuthorityValidationSchema } from 'app/edozvola/cases/special-regulations-compliance/model/special-regulations-compliance.schema';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Loader } from 'app/shared/loader/loader';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import {
  fetchCasePublicAuthorityOptionsQuery,
  fetchJptActivityOptionsQuery, fetchLitigationOptionsQuery
} from 'app/edozvola/codebooks/codebooks.queries';

export interface ICasePublicAuthorityFormProps {
  idCasePublicAuthority: string | null;
  setIdCasePublicAuthority: any;
  readOnly: boolean;
}

const CasePublicAuthorityForm = (props: ICasePublicAuthorityFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();

  const { data: publicAuthorityOptions, isLoading: loadingPublicAuthorityOptions } = fetchCasePublicAuthorityOptionsQuery(uuid);
  const { data: jptActivityOptions, isLoading: loadingJptActivityOptions } = fetchJptActivityOptionsQuery();
  const { data: litigationOptions, isLoading: loadingLitigationOptions } = fetchLitigationOptionsQuery();

  const { data: casePublicAuthorityData, isLoading: casePublicAuthorityDataLoading } = fetchCasePublicAuthorityQuery(
    props.idCasePublicAuthority,
  );

  const formik = useFormik({
    initialValues: casePublicAuthorityData || casePublicAuthorityInitialData,
    onSubmit: values => saveCasePublicAuthorityMutation(values),
    validationSchema: casePublicAuthorityValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: saveCasePublicAuthorityMutation, isPending: savingCasePublicAuthority } = saveCasePublicAuthorityQuery(uuid, () =>
    closeModal(),
  );

  const closeModal = () => {
    props.setIdCasePublicAuthority(null);
    formik.resetForm({ values: casePublicAuthorityInitialData });
  };

  const addNewAct = () => {
    formik.setFieldValue('issuedActs', [...formik.values.issuedActs, issuedActInitialData]);
  };

  const removeAct = (index: number) => {
    formik.setFieldValue(
      'issuedActs',
      formik.values.issuedActs.filter((issuedAct, i) => i !== index),
    );
  };

  return (
    <>
      <Modal scrollable size={'xl'} isOpen={props.idCasePublicAuthority !== null}>
        {props.readOnly ? (
          <ModalHeader toggle={() => closeModal()}>Pregled podataka o javnopravnom tijelu</ModalHeader>
        ) : (
          <ModalHeader>{props.idCasePublicAuthority === '' ? 'Unos javnopravnog tijela' : 'Uređivanje javnopravnog tijela'}</ModalHeader>
        )}
        <ModalBody>
          <FormikProvider value={formik}>
            {casePublicAuthorityDataLoading && <Loader />}
            {!casePublicAuthorityDataLoading && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <div className={'form-header'}>Podaci o javnopravnom tijelu</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Naziv'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi naziv'}
                      onItemSelect={selected => formik.setFieldValue('publicAuthority', selected)}
                      selected={formik.values.publicAuthority}
                      labelKey={'value'}
                      options={publicAuthorityOptions}
                      disabled={props?.readOnly}
                      error={
                        !props.readOnly && Array.isArray(formik.values.publicAuthority) && formik.values.publicAuthority.length === 0
                          ? 'Obavezan podatak'
                          : ''
                      }
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'ID JPT'} />
                    <Input
                      value={formik.values.publicAuthority[0]?.idJpt ?? ''}
                      disabled
                      error={
                        (formik.errors.publicAuthority &&
                          formik.errors.publicAuthority[0] &&
                          (formik.errors.publicAuthority[0]['idJpt'] as string)) ??
                        ''
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'OIB'} />
                    <Input
                      value={formik.values.publicAuthority[0]?.oib ?? ''}
                      disabled
                      error={
                        (formik.errors.publicAuthority &&
                          formik.errors.publicAuthority[0] &&
                          (formik.errors.publicAuthority[0]['oib'] as string)) ??
                        ''
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Grad/Općina'} />
                    <Input
                      value={formik.values.publicAuthority[0]?.cityMunicipality ?? ''}
                      disabled
                      error={
                        (formik.errors.publicAuthority &&
                          formik.errors.publicAuthority[0] &&
                          (formik.errors.publicAuthority[0]['cityMunicipality'] as string)) ??
                        ''
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Naselje'} />
                    <Input
                      value={formik.values.publicAuthority[0]?.community ?? ''}
                      disabled
                      error={
                        (formik.errors.publicAuthority &&
                          formik.errors.publicAuthority[0] &&
                          (formik.errors.publicAuthority[0]['community'] as string)) ??
                        ''
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Poštanski broj'} />
                    <Input
                      value={formik.values.publicAuthority[0]?.postalCode ?? ''}
                      disabled
                      error={
                        (formik.errors.publicAuthority &&
                          formik.errors.publicAuthority[0] &&
                          (formik.errors.publicAuthority[0]['postalCode'] as string)) ??
                        ''
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Pošta'} />
                    <Input
                      value={formik.values.publicAuthority[0]?.postName ?? ''}
                      disabled
                      error={
                        (formik.errors.publicAuthority &&
                          formik.errors.publicAuthority[0] &&
                          (formik.errors.publicAuthority[0]['postName'] as string)) ??
                        ''
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Ulica'} />
                    <Input
                      value={formik.values.publicAuthority[0]?.street ?? ''}
                      disabled
                      error={
                        (formik.errors.publicAuthority &&
                          formik.errors.publicAuthority[0] &&
                          (formik.errors.publicAuthority[0]['street'] as string)) ??
                        ''
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Kućni broj'} />
                    <Input value={formik.values.publicAuthority[0]?.houseNumber ?? ''} disabled />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Email'} />
                    <Input value={formik.values.publicAuthority[0]?.email ?? ''} disabled />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Telefon'} />
                    <Input value={formik.values.publicAuthority[0]?.contactNumber ?? ''} disabled />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Posebni propis'} />
                    <Input
                      value={formik.values.publicAuthority[0]?.relevantRegulation ?? ''}
                      disabled
                      error={
                        (formik.errors.publicAuthority &&
                          formik.errors.publicAuthority[0] &&
                          (formik.errors.publicAuthority[0]['relevantRegulation'] as string)) ??
                        ''
                      }
                    />
                  </Col>
                </Row>
              </div>
            )}
            <hr />
            <div className={'form-header mb-3'}>Podaci o izdanim aktima</div>
            {formik.values.issuedActs.map((issuedAct, index) => (
              <div key={index} className={'d-flex justify-content-between flex-column gap-3 mt-3'}>
                <div className={'justify-content-between d-flex'}>
                  <div className={'form-header'}>{`Akt ${index + 1}`}</div>
                  {!props.readOnly && <Button prependIcon={'trash'} size={'lg'} onClick={() => removeAct(index)} />}
                </div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Aktivnost'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi aktivnost'}
                      onItemSelect={selected => formik.setFieldValue(`issuedActs.${index}.jptActivity`, selected)}
                      selected={issuedAct.jptActivity ?? []}
                      labelKey={'value'}
                      options={loadingJptActivityOptions ? [] : jptActivityOptions}
                      error={
                        formik.errors &&
                        formik.errors.issuedActs &&
                        formik.errors.issuedActs[index] &&
                        formik.errors.issuedActs[index]['jptActivity']
                      }
                      showErrorMessage
                      clearButton
                      disabled={props?.readOnly}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Naziv akta'} />
                    <Input
                      disabled={props.readOnly}
                      value={issuedAct.name}
                      error={
                        formik.errors &&
                        formik.errors.issuedActs &&
                        formik.errors.issuedActs[index] &&
                        formik.errors.issuedActs[index]['name']
                      }
                      maxLength={200}
                      onChange={event => formik.setFieldValue(`issuedActs.${index}.name`, event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Klasifikacijska oznaka'} />
                    <Input
                      disabled={props.readOnly}
                      value={issuedAct.classificationMark}
                      error={
                        formik.errors &&
                        formik.errors.issuedActs &&
                        formik.errors.issuedActs[index] &&
                        formik.errors.issuedActs[index]['classificationMark']
                      }
                      maxLength={100}
                      onChange={event => formik.setFieldValue(`issuedActs.${index}.classificationMark`, event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Urudžbeni broj'} />
                    <Input
                      disabled={props.readOnly}
                      error={
                        formik.errors &&
                        formik.errors.issuedActs &&
                        formik.errors.issuedActs[index] &&
                        formik.errors.issuedActs[index]['orderNumber']
                      }
                      value={issuedAct.orderNumber}
                      maxLength={100}
                      onChange={event => formik.setFieldValue(`issuedActs.${index}.orderNumber`, event.target.value)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Datum izdavanja'} />
                    <Field
                      id={`issuedActs.${index}.dateOfIssuing`}
                      name={`issuedActs.${index}.dateOfIssuing`}
                      render={({ field }) => (
                        <FormikDatepicker
                          field={field}
                          setFieldValue={formik.setFieldValue}
                          error={
                            formik.errors &&
                            formik.errors.issuedActs &&
                            formik.errors.issuedActs[index] &&
                            formik.errors.issuedActs[index]['dateOfIssuing']
                              ? formik.errors.issuedActs[index]['dateOfIssuing']
                              : ''
                          }
                          dateFormat={APP_DATE_FORMAT}
                          disabled={props.readOnly}
                        />
                      )}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Spor'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi spor'}
                      onItemSelect={selected => formik.setFieldValue(`issuedActs.${index}.litigation`, selected)}
                      selected={issuedAct.litigation}
                      labelKey={'value'}
                      options={loadingLitigationOptions ? [] : litigationOptions}
                      disabled={props?.readOnly}
                      clearButton
                    />
                  </Col>
                </Row>
                <hr />
              </div>
            ))}
            {!props.readOnly && <Button disabled label={'Dodaj akt'} color={'secondary'} size={'lg'} prependIcon={'plus'} onClick={addNewAct} />}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                disabled={!formik.dirty || !formik.isValid}
                isLoading={savingCasePublicAuthority}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CasePublicAuthorityForm;
